'use client';

/**
 * useNavigation Hook
 *
 * Manages navigation stack for multi-screen flows.
 * Provides forward navigation, back navigation, and reset functionality.
 *
 * @param initialScreen - Optional. When set, the flow starts on this screen
 *   instead of PURCHASE_DETAILS.
 *
 * @example
 * ```tsx
 * const { currentScreen, navigateTo, goBack, reset } = useNavigation();
 *
 * // Navigate to a new screen
 * navigateTo(SCREENS.PAYMENT_METHODS_LIST);
 *
 * // Go back (calls onRootBack if at first screen)
 * goBack(() => closeModal());
 *
 * // Reset to initial screen
 * reset();
 * ```
 */

import { useState, useCallback } from 'react';
import type { Screen } from '../types';
import { INITIAL_SCREEN } from '../utils/constants';

/** Options for useNavigation */
export interface UseNavigationOptions {
  /** When set, flow starts on this screen instead of the default */
  initialScreen?: Screen;
}

/** Return type for useNavigation hook */
export interface UseNavigationResult {
  /** Currently displayed screen */
  currentScreen: Screen;
  /** Full navigation history stack */
  screenStack: Screen[];
  /** Navigate to a new screen (pushes to stack) */
  navigateTo: (screen: Screen) => void;
  /** Go back to previous screen (pops from stack) */
  goBack: (onRootBack?: () => void) => void;
  /** Reset navigation to initial screen */
  reset: () => void;
}

export const useNavigation = (options?: UseNavigationOptions): UseNavigationResult => {
  const startScreen = options?.initialScreen ?? INITIAL_SCREEN;
  const [currentScreen, setCurrentScreen] = useState<Screen>(startScreen);
  const [screenStack, setScreenStack] = useState<Screen[]>([startScreen]);

  const navigateTo = useCallback((screen: Screen) => {
    setCurrentScreen(screen);
    setScreenStack(prev => [...prev, screen]);
  }, []);

  const goBack = useCallback((onRootBack?: () => void) => {
    setScreenStack(prev => {
      if (prev.length <= 1) {
        // Already at root screen
        onRootBack?.();
        return prev;
      }

      // Pop from stack
      const newStack = [...prev];
      newStack.pop();
      const previousScreen = newStack[newStack.length - 1];
      setCurrentScreen(previousScreen);
      return newStack;
    });
  }, []);

  const reset = useCallback(() => {
    setCurrentScreen(startScreen);
    setScreenStack([startScreen]);
  }, [startScreen]);

  return {
    currentScreen,
    screenStack,
    navigateTo,
    goBack,
    reset
  };
};
